<template>
	<transition name="modal-fade">
		<div v-if="isOpen" class="course-modal" @click.self="handleClose">
			<div class="course-modal__container">
				<!-- Заголовок -->
				<div class="course-modal__header">
					<h2 class="course-modal__title">Управление курсом</h2>
					<button
						type="button"
						class="course-modal__close"
						@click="handleClose"
						aria-label="Закрыть"
					>
						✕
					</button>
				</div>

				<!-- Информация о курсе -->
				<div v-if="courseData" class="course-modal__course-info">
					<h3 class="course-modal__course-title">{{ courseData.title }}</h3>
					<div class="course-modal__course-meta">
						<span>Категория: {{ formatCourseCategory(courseData.category) }}</span>
						<span>Модулей: {{ modules.length }}</span>
						<span>Студентов: {{ courseData.participants || 0 }}</span>
					</div>
				</div>

				<!-- Состояние загрузки -->
				<div v-if="loading" class="course-modal__loading">
					<div class="course-modal__spinner"></div>
					<p>Загрузка данных курса...</p>
				</div>

				<!-- Ошибка загрузки -->
				<div v-else-if="error" class="course-modal__error">
					<p>{{ error }}</p>
					<button
						type="button"
						class="course-modal__retry-btn"
						@click="loadCourseData"
					>
						Повторить попытку
					</button>
				</div>

				<!-- Контент модального окна -->
				<div v-else class="course-modal__content">
					<!-- Список модулей -->
					<div v-if="modules.length === 0" class="course-modal__empty">
						<p>В курсе пока нет модулей</p>
					</div>

					<div v-else class="course-modal__modules">
						<!-- Модуль -->
						<div
							v-for="(module, moduleIndex) in modules"
							:key="module.id"
							class="course-modal__module"
						>
							<!-- Заголовок модуля -->
							<div class="course-modal__module-header">
								<div class="course-modal__module-info">
									<h4 class="course-modal__module-title">
										{{ moduleIndex + 1 }}. {{ module.title }}
									</h4>
									<p
										v-if="module.subtitle"
										class="course-modal__module-subtitle"
									>
										{{ module.subtitle }}
									</p>
								</div>
								<button
									type="button"
									class="course-modal__toggle-btn"
									@click="toggleModule(module.id)"
								>
									{{ expandedModules.includes(module.id) ? '▼' : '▶' }}
								</button>
							</div>

							<!-- Содержимое модуля (раскрывающееся) -->
							<transition name="expand">
								<div
									v-if="expandedModules.includes(module.id)"
									class="course-modal__module-content"
								>
									<!-- Вкладки элементов модуля -->
									<div class="course-modal__tabs">
										<button
											v-for="tab in moduleTabs"
											:key="tab.id"
											type="button"
											class="course-modal__tab"
											:class="{
												'course-modal__tab--active':
													activeModuleTabs[module.id] === tab.id,
											}"
											@click="setActiveTab(module.id, tab.id)"
										>
											{{ tab.label }}
											<span class="course-modal__tab-count">
												{{ getElementsCount(module, tab.id) }}
											</span>
										</button>
									</div>

									<!-- Список элементов -->
									<div class="course-modal__elements">
										<div
											v-if="
												getCurrentElements(module, activeModuleTabs[module.id])
													.length === 0
											"
											class="course-modal__elements-empty"
										>
											Нет элементов
										</div>

										<div
											v-for="element in getCurrentElements(
												module,
												activeModuleTabs[module.id]
											)"
											:key="element.id"
											class="course-modal__element"
										>
											<div class="course-modal__element-info">
												<h5 class="course-modal__element-title">
													{{ element.title }}
												</h5>
												<div class="course-modal__element-meta">
													<span v-if="element.duration">
														⏱️ {{ element.duration }} мин
													</span>
													<span v-if="element.questionsCount">
														❓ {{ element.questionsCount }} вопросов
													</span>
													<span v-if="element.passingScore">
														✅ {{ element.passingScore }}% для прохождения
													</span>
													<span
														v-if="element.isDraft"
														class="course-modal__element-badge course-modal__element-badge--draft"
													>
														Черновик
													</span>
													<span
														v-if="element.isHidden"
														class="course-modal__element-badge course-modal__element-badge--hidden"
													>
														Скрыт
													</span>
												</div>
											</div>
											<button
												type="button"
												class="course-modal__edit-btn"
												@click="
													handleEditElement(
														element,
														activeModuleTabs[module.id]
													)
												"
											>
												✏️ Редактировать
											</button>
										</div>
									</div>
								</div>
							</transition>
						</div>
					</div>
				</div>

				<!-- Футер с кнопками -->
				<div class="course-modal__footer">
					<button
						type="button"
						class="course-modal__btn course-modal__btn--secondary"
						@click="handleClose"
					>
						Закрыть
					</button>
				</div>
			</div>
		</div>
	</transition>

	<!-- Модальное окно редактирования элемента -->
	<EditMaterialModal
		:is-open="showEditModal"
		:material="editingElement"
		:material-type="editingElementType"
		@close="handleCloseEditModal"
		@save="handleSaveElement"
		@save-draft="handleSaveDraft"
		@preview="handlePreviewElement"
	/>
</template>

<script>
import EditMaterialModal from './EditMaterialModal.vue'
import { modulesService } from '@/services'
import { getCourseCategoryLabel } from '@/utils/courseCategories'

export default {
	name: 'CourseManagementModal',
	components: {
		EditMaterialModal,
	},
	props: {
		isOpen: {
			type: Boolean,
			default: false,
		},
		courseId: {
			type: [Number, String],
			default: null,
		},
		courseData: {
			type: Object,
			default: null,
		},
	},
	emits: ['close', 'element-updated'],
	data() {
		return {
			loading: false,
			error: null,
			modules: [],
			expandedModules: [],
			activeModuleTabs: {},
			moduleTabs: [
				{ id: 'lessons', label: 'Уроки' },
				{ id: 'practice', label: 'Практические задания' },
				{ id: 'homework', label: 'Домашние работы' },
				{ id: 'tests', label: 'Тесты' },
			],
			showEditModal: false,
			editingElement: null,
			editingElementType: null,
		}
	},
	watch: {
		isOpen(newValue) {
			if (newValue && this.courseId) {
				this.loadCourseData()
			}
		},
		courseId(newValue) {
			if (newValue && this.isOpen) {
				this.loadCourseData()
			}
		},
	},
	methods: {
		formatCourseCategory(category) {
			return getCourseCategoryLabel(category)
		},
		handleClose() {
			this.$emit('close')
		},

		async loadCourseData() {
			if (!this.courseId) return

			this.loading = true
			this.error = null

			try {
				console.log(`🔍 Загрузка данных курса ${this.courseId}...`)

				// Загружаем модули курса со всеми элементами
				const modules = await modulesService.getCourseModulesWithElements(
					this.courseId
				)

				console.log(`📦 Получено модулей: ${modules.length}`, modules)

				// Если модулей нет - это не ошибка, просто пустой курс
				if (!modules || modules.length === 0) {
					console.log('ℹ️ В курсе пока нет модулей')
					this.modules = []
					this.loading = false
					return
				}

				// Преобразуем данные для отображения
				this.modules = modules.map(module => ({
					...module,
					lessons: (module.lessons || []).map(lesson => ({
						...lesson,
						isDraft: lesson.is_draft || lesson.isDraft || false,
						isHidden: lesson.is_hidden || lesson.isHidden || false,
					})),
					tests: (module.tests || []).map(test => ({
						...test,
						isDraft: test.is_draft || test.isDraft || false,
						isHidden: test.is_hidden || test.isHidden || false,
						questionsCount: test.questions_count || test.questionsCount || 0,
						passingScore: test.passing_score || test.passingScore || 0,
					})),
					homework: (module.homework || []).map(hw => ({
						...hw,
						isDraft: hw.is_draft || hw.isDraft || false,
						isHidden: hw.is_hidden || hw.isHidden || false,
					})),
					practice: (module.practice || []).map(pr => ({
						...pr,
						isDraft: pr.is_draft || pr.isDraft || false,
						isHidden: pr.is_hidden || pr.isHidden || false,
					})),
				}))

				console.log('✅ Модули успешно преобразованы:', this.modules)

				// Инициализация активных вкладок для каждого модуля
				this.modules.forEach(module => {
					if (!this.activeModuleTabs[module.id]) {
						this.activeModuleTabs[module.id] = 'lessons'
					}
				})

				// Автоматически раскрываем первый модуль
				if (this.modules.length > 0 && this.expandedModules.length === 0) {
					this.expandedModules.push(this.modules[0].id)
				}
			} catch (err) {
				console.error('❌ Ошибка загрузки данных курса:', err)
				this.error = `Ошибка при получении модулей курса: ${
					err.message || 'Неизвестная ошибка'
				}`
			} finally {
				this.loading = false
			}
		},

		toggleModule(moduleId) {
			const index = this.expandedModules.indexOf(moduleId)
			if (index > -1) {
				this.expandedModules.splice(index, 1)
			} else {
				this.expandedModules.push(moduleId)
			}
		},

		setActiveTab(moduleId, tabId) {
			this.activeModuleTabs[moduleId] = tabId
		},

		getElementsCount(module, tabId) {
			const elements = module[tabId] || []
			return elements.length
		},

		getCurrentElements(module, tabId) {
			return module[tabId] || []
		},

		handleEditElement(element, type) {
			this.editingElement = element
			this.editingElementType =
				type === 'lessons' ? 'lesson' : type.slice(0, -1)
			this.showEditModal = true
		},

		handleCloseEditModal() {
			this.showEditModal = false
			this.editingElement = null
			this.editingElementType = null
		},

		handleSaveElement(data) {
			console.log('Сохранение элемента:', data)
			this.$emit('element-updated', data)
			this.handleCloseEditModal()
			// TODO: Обновить данные в списке
		},

		handleSaveDraft(data) {
			console.log('Сохранение черновика:', data)
			this.handleCloseEditModal()
			// TODO: Обновить данные в списке
		},

		handlePreviewElement(data) {
			console.log('Предпросмотр элемента:', data)
			// TODO: Открыть предпросмотр
		},
	},
}
</script>

<style scoped>
/* Модальное окно */
.course-modal {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
	padding: 20px;
	overflow-y: auto;
}

.course-modal__container {
	background: #ffffff;
	border-radius: 20px;
	max-width: 1200px;
	width: 100%;
	max-height: 90vh;
	display: flex;
	flex-direction: column;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

/* Заголовок */
.course-modal__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 30px 40px;
	border-bottom: 1px solid #e0e0e0;
}

.course-modal__title {
	color: #000000;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 28px;
	font-weight: 700;
	margin: 0;
}

.course-modal__close {
	background: transparent;
	border: none;
	color: #636363;
	cursor: pointer;
	font-size: 32px;
	line-height: 1;
	padding: 0;
	transition: color 0.2s ease;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.course-modal__close:hover {
	color: #000000;
}

/* Информация о курсе */
.course-modal__course-info {
	padding: 20px 40px;
	background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
	border-bottom: 1px solid #e0e0e0;
}

.course-modal__course-title {
	color: #000000;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 20px;
	font-weight: 600;
	margin: 0 0 10px 0;
}

.course-modal__course-meta {
	display: flex;
	gap: 20px;
	color: #636363;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 14px;
}

/* Состояния загрузки и ошибок */
.course-modal__loading,
.course-modal__error,
.course-modal__empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 40px;
	color: #636363;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 16px;
}

.course-modal__spinner {
	width: 50px;
	height: 50px;
	border: 4px solid #e0e0e0;
	border-top-color: #0095ec;
	border-radius: 50%;
	animation: spin 1s linear infinite;
	margin-bottom: 20px;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

.course-modal__error {
	color: #ff4757;
}

.course-modal__retry-btn {
	margin-top: 20px;
	padding: 10px 20px;
	background: #0095ec;
	color: #ffffff;
	border: none;
	border-radius: 8px;
	cursor: pointer;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 14px;
	font-weight: 600;
	transition: background 0.2s ease;
}

.course-modal__retry-btn:hover {
	background: #0085d4;
}

/* Контент */
.course-modal__content {
	flex: 1;
	overflow-y: auto;
	padding: 30px 40px;
}

/* Модули */
.course-modal__modules {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.course-modal__module {
	background: #f9f9f9;
	border-radius: 12px;
	overflow: hidden;
	border: 1px solid #e0e0e0;
}

.course-modal__module-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 20px;
	background: #ffffff;
	cursor: pointer;
	transition: background 0.2s ease;
}

.course-modal__module-header:hover {
	background: #f5f5f5;
}

.course-modal__module-info {
	flex: 1;
}

.course-modal__module-title {
	color: #000000;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 18px;
	font-weight: 600;
	margin: 0 0 5px 0;
}

.course-modal__module-subtitle {
	color: #636363;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 14px;
	margin: 0;
}

.course-modal__toggle-btn {
	background: transparent;
	border: none;
	color: #0095ec;
	cursor: pointer;
	font-size: 20px;
	padding: 5px 10px;
	transition: transform 0.2s ease;
}

/* Содержимое модуля */
.course-modal__module-content {
	padding: 20px;
}

/* Вкладки */
.course-modal__tabs {
	display: flex;
	gap: 10px;
	margin-bottom: 20px;
	border-bottom: 2px solid #e0e0e0;
}

.course-modal__tab {
	background: transparent;
	border: none;
	border-bottom: 3px solid transparent;
	color: #636363;
	cursor: pointer;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 14px;
	font-weight: 500;
	padding: 10px 15px;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	gap: 8px;
}

.course-modal__tab:hover {
	color: #0095ec;
}

.course-modal__tab--active {
	color: #0095ec;
	border-bottom-color: #0095ec;
	font-weight: 600;
}

.course-modal__tab-count {
	background: #e0e0e0;
	border-radius: 12px;
	color: #636363;
	font-size: 12px;
	padding: 2px 8px;
}

.course-modal__tab--active .course-modal__tab-count {
	background: #0095ec;
	color: #ffffff;
}

/* Элементы */
.course-modal__elements {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.course-modal__elements-empty {
	color: #999999;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 14px;
	padding: 30px;
	text-align: center;
}

.course-modal__element {
	background: #ffffff;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 15px 20px;
	transition: all 0.2s ease;
}

.course-modal__element:hover {
	border-color: #0095ec;
	box-shadow: 0 2px 8px rgba(0, 149, 236, 0.1);
}

.course-modal__element-info {
	flex: 1;
}

.course-modal__element-title {
	color: #000000;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 16px;
	font-weight: 600;
	margin: 0 0 8px 0;
}

.course-modal__element-meta {
	display: flex;
	gap: 15px;
	color: #636363;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 13px;
	flex-wrap: wrap;
}

.course-modal__element-badge {
	padding: 2px 8px;
	border-radius: 4px;
	font-size: 11px;
	font-weight: 600;
}

.course-modal__element-badge--draft {
	background: #ffa500;
	color: #ffffff;
}

.course-modal__element-badge--hidden {
	background: #ff4757;
	color: #ffffff;
}

.course-modal__edit-btn {
	background: #0095ec;
	border: none;
	border-radius: 8px;
	color: #ffffff;
	cursor: pointer;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 14px;
	font-weight: 600;
	padding: 10px 20px;
	transition: all 0.2s ease;
	white-space: nowrap;
}

.course-modal__edit-btn:hover {
	background: #0085d4;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.3);
}

/* Футер */
.course-modal__footer {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 15px;
	padding: 20px 40px;
	border-top: 1px solid #e0e0e0;
}

.course-modal__btn {
	border: none;
	border-radius: 8px;
	cursor: pointer;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 14px;
	font-weight: 600;
	padding: 12px 24px;
	transition: all 0.2s ease;
}

.course-modal__btn--secondary {
	background: #e0e0e0;
	color: #636363;
}

.course-modal__btn--secondary:hover {
	background: #d0d0d0;
}

/* Анимации */
.modal-fade-enter-active,
.modal-fade-leave-active {
	transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
	opacity: 0;
}

.modal-fade-enter-active .course-modal__container,
.modal-fade-leave-active .course-modal__container {
	transition: transform 0.3s ease;
}

.modal-fade-enter-from .course-modal__container,
.modal-fade-leave-to .course-modal__container {
	transform: scale(0.9);
}

.expand-enter-active,
.expand-leave-active {
	transition: all 0.3s ease;
	overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
	max-height: 0;
	opacity: 0;
}

.expand-enter-to,
.expand-leave-from {
	max-height: 1000px;
	opacity: 1;
}
</style>
